import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Calendar, Award, Sparkles, Theater, Music, Heart, Volume2, VolumeX } from 'lucide-react';
import { playSoundIfEnabled } from '../utils/sounds';
import { workshops, testimonials } from '../mock/mockData';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    playSoundIfEnabled('curtain');
    
    // Initialize background music
    const audio = new Audio('https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/9ts4aae2_jean-paul-v-bienvenue-au-cirque-309565.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    audioRef.current = audio;
    
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []); // Empty deps is correct - only run on mount

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.pause();
        setIsMusicPlaying(false);
      } else {
        audioRef.current.play().catch(() => {
          // Audio playback blocked by browser
        });
        setIsMusicPlaying(true);
      }
    }
  };

  const handleClick = (soundType = 'click') => {
    playSoundIfEnabled(soundType);
  };

  const getThemeForSeason = (season) => {
    const themes = {
      'Summer': 'summer',
      'Christmas': 'christmas',
      'Halloween': 'halloween',
      'Easter': 'easter'
    };
    return themes[season] || 'general';
  };

  const featuredWorkshops = workshops.filter(w => w.season === 'Summer' || w.season === 'Halloween' || w.season === 'Christmas');

  return (
    <div className="min-h-screen bg-black">
      {/* Floating Music Control Button */}
      <button
        onClick={toggleMusic}
        className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-light-pink to-mid-purple p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 group"
        aria-label={isMusicPlaying ? 'Pause music' : 'Play music'}
      >
        {isMusicPlaying ? (
          <Volume2 className="w-6 h-6 text-white animate-pulse" />
        ) : (
          <VolumeX className="w-6 h-6 text-white" />
        )}
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black/80 text-white px-3 py-1 rounded text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          {isMusicPlaying ? 'Pause Music' : 'Play Music'}
        </span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-32 h-32 bg-light-pink/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-mid-purple/20 rounded-full blur-3xl animate-pulse delay-700" />
          <div className="absolute top-1/2 left-1/3 w-36 h-36 bg-light-yellow/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div 
              className={`transform transition-all duration-1000 ${
                isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
              }`}
            >
              <div className="inline-flex items-center gap-2 bg-light-pink/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-light-pink/20">
                <Sparkles className="w-4 h-4 text-light-pink animate-pulse" />
                <span className="text-light-pink text-sm font-mono uppercase tracking-wider">
                  Oxford's Premier Drama Studio
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight">
                INSPIRE
                <br />
                <span className="text-light-pink">STUDIO</span>
                <br />
                <span className="text-mid-yellow">OXFORD</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-mid-grey mb-8 leading-relaxed">
                Where young actors discover their stage through Shakespeare, drama, and creative expression
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/workshops"
                  className="cta-button large bg-light-pink text-black hover:bg-mid-yellow hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2 group"
                  onClick={() => handleClick('magic')}
                >
                  Explore Workshops
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/about"
                  className="cta-button large bg-transparent border-2 border-light-pink text-light-pink hover:bg-light-pink hover:text-black transition-all duration-300 inline-flex items-center justify-center"
                  onClick={() => handleClick('click')}
                >
                  Learn More
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-light-pink/20">
                <div className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-light-pink mb-2">150+</div>
                  <div className="text-sm text-mid-grey">Young Actors</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-mid-yellow mb-2">12+</div>
                  <div className="text-sm text-mid-grey">Workshops Yearly</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-mid-purple mb-2">5★</div>
                  <div className="text-sm text-mid-grey">Parent Reviews</div>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div 
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
              }`}
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-light-pink/30 to-mid-purple/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/lr9d39cm_420D9E0D-2267-44E2-90DD-199105493BCE.jpeg"
                  alt="INSPIRE STUDIO OXFORD - Young Actor"
                  className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover border-4 border-light-pink/20 group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-md rounded-2xl p-4 border border-light-pink/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-light-pink flex items-center justify-center">
                      <Theater className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Our Summer Workshops</div>
                      <div className="text-light-pink text-sm">Real Performances, Real Confidence!</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-light-pink/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-light-pink rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Featured Workshops */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-mid-purple/10 rounded-full px-4 py-2 mb-4">
              <Calendar className="w-4 h-4 text-mid-purple" />
              <span className="text-mid-purple text-sm font-mono uppercase tracking-wider">
                Upcoming Workshops
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
              2026 Workshops
            </h2>
            <p className="text-xl text-mid-grey max-w-2xl mx-auto">
              Professional drama training with Shakespeare, musicals, and creative productions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredWorkshops.map((workshop, index) => (
              <div
                key={workshop.id}
                className="group relative transform transition-all duration-500 hover:-translate-y-2"
                style={{ 
                  animationDelay: `${index * 150}ms`,
                  opacity: 0,
                  animation: 'slideInUp 0.6s ease-out forwards'
                }}
              >
                <div className={`relative rounded-2xl p-6 border-2 border-transparent hover:border-light-pink/50 transition-all duration-300 overflow-hidden h-full bg-${workshop.color}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-black/5 rounded-full blur-2xl" />
                  
                  {/* Status Badge */}
                  {workshop.status === 'sold-out' && (
                    <div className="absolute top-4 right-4 status-badge status-sold-out text-xs">
                      SOLD OUT
                    </div>
                  )}
                  {workshop.status === 'opening-soon' && (
                    <div className="absolute top-4 right-4 status-badge status-opening-soon text-xs">
                      OPENING SOON
                    </div>
                  )}
                  
                  <div className="relative z-10">
                    <div className="mb-4">
                      <span className="inline-block bg-black/20 backdrop-blur-sm text-black text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full">
                        {workshop.ageGroup}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-black mb-2 group-hover:text-black/80 transition-colors">
                      {workshop.title}
                    </h3>
                    
                    <p className="text-black/70 text-sm mb-4">
                      {workshop.dates}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-black/10">
                      <div className="text-2xl font-bold text-black">£{workshop.price}</div>
                      {workshop.status === 'sold-out' ? (
                        <div className="text-xs text-black/70 font-semibold">Fully Booked</div>
                      ) : workshop.status === 'opening-soon' ? (
                        <div className="text-xs text-black/70 font-semibold">Coming Soon</div>
                      ) : (
                        <div className="text-sm text-black/70">{workshop.spotsLeft} spots left</div>
                      )}
                    </div>
                    
                    {workshop.status !== 'sold-out' && workshop.status !== 'opening-soon' && (
                      <Link
                        to={`/workshops`}
                        className="inline-flex items-center gap-2 text-black font-semibold hover:gap-3 transition-all duration-300 group/link"
                        onClick={() => handleClick(getThemeForSeason(workshop.season))}
                      >
                        Book Now
                        <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/workshops"
              className="cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 inline-flex items-center gap-2"
              onClick={() => handleClick('magic')}
            >
              View All Workshops
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-64 h-64 bg-mid-purple/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-light-yellow/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-light-yellow/10 rounded-full px-4 py-2 mb-4">
              <Star className="w-4 h-4 text-light-yellow" />
              <span className="text-light-yellow text-sm font-mono uppercase tracking-wider">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
              The INSPIRE Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Small Group Sizes',
                description: 'Maximum 35 children per workshop ensures individual attention and support for every young actor.',
                color: 'light-pink'
              },
              {
                icon: <Theater className="w-8 h-8" />,
                title: 'Professional Training',
                description: 'Learn from Richard Kidd, trained at Guildhall School of Music & Drama with 11 years professional acting experience.',
                color: 'mid-purple'
              },
              {
                icon: <Music className="w-8 h-8" />,
                title: 'Shakespeare Made Fun',
                description: 'Age-appropriate adaptations that make the Bard accessible, engaging, and exciting for all ages.',
                color: 'light-yellow'
              },
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: 'Confidence Building',
                description: 'Watch your child grow in confidence, creativity, and communication skills in a supportive environment.',
                color: 'mid-blue'
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: 'Performance Opportunity',
                description: 'Every workshop ends with a showcase where children can shine and parents can celebrate their achievements.',
                color: 'mid-green'
              },
              {
                icon: <Calendar className="w-8 h-8" />,
                title: 'Convenient Scheduling',
                description: 'Workshops during all school holidays - Easter, Summer, Halloween, and Christmas.',
                color: 'mid-orange'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-light-pink/50 transition-all duration-300 hover:-translate-y-2"
                onMouseEnter={() => handleClick('hover')}
              >
                <div className={`w-16 h-16 bg-${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 text-black`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-mid-grey leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Special Features - Halloween Film & Community */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Halloween Film Project */}
            <div className="relative overflow-hidden rounded-3xl border-2 border-mid-purple/30 hover:border-mid-purple/60 transition-all duration-300 group">
              <div className="absolute inset-0 bg-gradient-to-br from-mid-purple/20 to-black" />
              <div className="relative p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-mid-purple rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Annual Halloween Film Project</h3>
                </div>
                <p className="text-mid-grey mb-6 leading-relaxed">
                  Every year, we create a professional Halloween film with our students! Learn cinematography, 
                  lighting, and performance skills while making a movie you'll treasure forever.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/9gr297lc_IMG_3346.jpeg"
                    alt="Halloween film production"
                    className="rounded-xl object-cover h-32 w-full"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/iz2ysumk_IMG_3347.jpeg"
                    alt="Film lighting"
                    className="rounded-xl object-cover h-32 w-full"
                  />
                </div>
              </div>
            </div>

            {/* Community Impact */}
            <div className="relative overflow-hidden rounded-3xl border-2 border-light-pink/30 hover:border-light-pink/60 transition-all duration-300 group">
              <div className="absolute inset-0 bg-gradient-to-br from-light-pink/20 to-black" />
              <div className="relative p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-light-pink rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-black" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Community Giving Back</h3>
                </div>
                <p className="text-mid-grey mb-6 leading-relaxed">
                  We believe in teaching children the importance of community. Our students organize charity 
                  drives, including collecting for The Gate House homeless shelter at Christmas.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/bkt1t9g2_IMG_8826.jpeg"
                    alt="Christmas charity"
                    className="rounded-xl object-cover h-32 w-full"
                  />
                  <img 
                    src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/t60hitzm_8CD1BAE2-5E72-4A44-934A-E1AB772AAB1E.jpeg"
                    alt="Food bank donation"
                    className="rounded-xl object-cover h-32 w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-black to-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-light-pink/10 rounded-full px-4 py-2 mb-4">
              <Star className="w-4 h-4 text-light-pink fill-light-pink" />
              <span className="text-light-pink text-sm font-mono uppercase tracking-wider">
                Parent Reviews
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-4">
              What Parents Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-light-pink/50 transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-light-yellow fill-light-yellow" />
                  ))}
                </div>
                <p className="text-white text-lg mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div>
                    <div className="text-light-pink font-semibold">{testimonial.parent}</div>
                    <div className="text-mid-grey text-sm">{testimonial.child}</div>
                  </div>
                  <div className="text-xs text-mid-grey">{testimonial.workshop}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Featured Gallery Preview */}
          <div className="mb-12">
            <h3 className="text-3xl font-display font-bold text-white text-center mb-8">
              See Our Workshops in Action
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="relative group overflow-hidden rounded-2xl aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/75q2pyhu_IMG_6274.jpeg"
                  alt="Workshop moment"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              </div>
              <div className="relative group overflow-hidden rounded-2xl aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/yzvrp2k0_IMG_8101.jpeg"
                  alt="Halloween workshop"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              </div>
              <div className="relative group overflow-hidden rounded-2xl aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/f6zbs83r_3364A91D-C544-4EE4-8A48-CD3DB33A7825.jpeg"
                  alt="Creative activities"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              </div>
              <div className="relative group overflow-hidden rounded-2xl aspect-square">
                <img 
                  src="https://customer-assets.emergentagent.com/job_drama-kids-workshops/artifacts/bkt1t9g2_IMG_8826.jpeg"
                  alt="Christmas charity"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              </div>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/gallery"
                className="cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 inline-flex items-center gap-2"
                onClick={() => handleClick('magic')}
              >
                View Full Gallery
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-light-pink/20 via-mid-purple/20 to-light-yellow/20" />
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <Sparkles className="w-16 h-16 text-light-pink mx-auto mb-6 animate-pulse" />
          <h2 className="text-4xl lg:text-6xl font-display font-bold text-white mb-6">
            Ready to Inspire Your Child?
          </h2>
          <p className="text-xl text-mid-grey mb-8 max-w-2xl mx-auto">
            Join us for an unforgettable drama experience where creativity, confidence, and Shakespeare come alive!
          </p>
          <Link
            to="/workshops"
            className="cta-button large bg-light-pink text-black hover:bg-mid-yellow hover:scale-110 transition-all duration-300 inline-flex items-center gap-2"
            onClick={() => handleClick('success')}
          >
            Book Your Workshop Today
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
